/**
 * Google Drive API Service - SHARING MODULE
 *
 * Permissions, collaboration, and sharing methods.
 * Handles biblioteca sharing, PDF sharing, and permission management.
 */

import { GoogleDriveServiceRepository } from './repository';
import { DRIVE_API_BASE } from './core';
import { driveFetch } from './driveFetch';
import { logger } from '../../utils/logger';

export class GoogleDriveService extends GoogleDriveServiceRepository {

  // ============================================
  // COMPARTILHAMENTO AVANÇADO - v8.0
  // ============================================

  /**
   * Compartilhar arquivo/pasta com email específico
   * @param fileId - ID do arquivo ou pasta
   * @param email - Email do destinatário
   * @param role - 'reader' | 'writer' | 'commenter'
   * @returns Permission ID ou null se falhar
   */
  async shareWithEmail(
    fileId: string,
    email: string,
    role: 'reader' | 'writer' | 'commenter' = 'reader'
  ): Promise<string | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const permissionUrl = `${DRIVE_API_BASE}/files/${fileId}/permissions?sendNotificationEmail=true`;

    try {
      const response = await driveFetch(permissionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          role: role,
          type: 'user',
          emailAddress: email
        })
      });

      if (!response.ok) {
        const error = await response.json();
        logger.error('GoogleDriveSharing', 'Erro ao compartilhar:', error);
        throw new Error(error.error?.message || 'Failed to share');
      }

      const data = await response.json();
      return data.id;
    } catch (error) {
      logger.error('GoogleDriveSharing', 'shareWithEmail error:', error);
      throw error;
    }
  }

  /**
   * Listar todas as permissões de um arquivo/pasta
   * @param fileId - ID do arquivo ou pasta
   * @returns Lista de permissões
   */
  async getFilePermissions(fileId: string): Promise<Array<{
    id: string;
    email: string;
    displayName?: string;
    role: string;
    type: string;
    photoLink?: string;
  }>> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const permissionsUrl = `${DRIVE_API_BASE}/files/${fileId}/permissions?fields=permissions(id,emailAddress,displayName,role,type,photoLink)`;

    try {
      const response = await driveFetch(permissionsUrl, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get permissions: ${response.statusText}`);
      }

      const data = await response.json();

      return (data.permissions || []).map((p: { id: string; emailAddress?: string; displayName?: string; role: string; type: string; photoLink?: string }) => ({
        id: p.id,
        email: p.emailAddress || '',
        displayName: p.displayName,
        role: p.role,
        type: p.type,
        photoLink: p.photoLink
      }));
    } catch (error) {
      logger.error('GoogleDriveSharing', 'getFilePermissions error:', error);
      return [];
    }
  }

  /**
   * Remover permissão de um arquivo/pasta
   * @param fileId - ID do arquivo ou pasta
   * @param permissionId - ID da permissão a remover
   */
  async removePermission(fileId: string, permissionId: string): Promise<boolean> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const deleteUrl = `${DRIVE_API_BASE}/files/${fileId}/permissions/${permissionId}`;

    try {
      const response = await driveFetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok && response.status !== 204) {
        throw new Error(`Failed to remove permission: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      logger.error('GoogleDriveSharing', 'removePermission error:', error);
      return false;
    }
  }

  /**
   * Atualizar papel de uma permissão
   * @param fileId - ID do arquivo ou pasta
   * @param permissionId - ID da permissão
   * @param newRole - Novo papel: 'reader' | 'writer' | 'commenter'
   */
  async updatePermissionRole(
    fileId: string,
    permissionId: string,
    newRole: 'reader' | 'writer' | 'commenter'
  ): Promise<boolean> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const updateUrl = `${DRIVE_API_BASE}/files/${fileId}/permissions/${permissionId}`;

    try {
      const response = await driveFetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) {
        throw new Error(`Failed to update permission: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      logger.error('GoogleDriveSharing', 'updatePermissionRole error:', error);
      return false;
    }
  }

  // ============================================
  // BIBLIOTECA SHARING
  // ============================================

  /**
   * Obter ID da pasta Biblioteca
   * Útil para compartilhar a biblioteca inteira
   */
  async getBibliotecaFolderId(): Promise<string | null> {
    try {
      if (!this.appFolderId) {
        await this.ensureAppStructure();
      }

      if (!this.appFolderId) return null;

      const bibliotecaId = await this.findOrCreateSubfolder('Biblioteca');
      return bibliotecaId;
    } catch (error) {
      logger.error('GoogleDriveSharing', 'getBibliotecaFolderId error:', error);
      return null;
    }
  }

  /**
   * Verificar se a biblioteca está compartilhada
   * Retorna lista de colaboradores (excluindo owner)
   */
  async getBibliotecaCollaborators(): Promise<Array<{
    id: string;
    email: string;
    displayName?: string;
    role: string;
    photoLink?: string;
  }>> {
    const bibliotecaId = await this.getBibliotecaFolderId();
    if (!bibliotecaId) return [];

    const permissions = await this.getFilePermissions(bibliotecaId);

    // Filtrar apenas permissões de usuários (não 'anyone' ou 'domain')
    // e excluir o owner
    return permissions.filter(p =>
      p.type === 'user' && p.role !== 'owner'
    );
  }

  /**
   * Compartilhar biblioteca com um colaborador
   * @param email - Email do colaborador
   * @param role - 'editor' (writer) | 'viewer' (reader)
   */
  async shareBiblioteca(
    email: string,
    role: 'editor' | 'viewer'
  ): Promise<string | null> {
    const bibliotecaId = await this.getBibliotecaFolderId();
    if (!bibliotecaId) {
      throw new Error('Biblioteca folder not found');
    }

    // Mapear role do Bigwa para role do Google Drive
    const driveRole = role === 'editor' ? 'writer' : 'reader';

    return await this.shareWithEmail(bibliotecaId, email, driveRole);
  }

  /**
   * Remover colaborador da biblioteca
   * @param permissionId - ID da permissão do colaborador
   */
  async removeBibliotecaCollaborator(permissionId: string): Promise<boolean> {
    const bibliotecaId = await this.getBibliotecaFolderId();
    if (!bibliotecaId) return false;

    return await this.removePermission(bibliotecaId, permissionId);
  }

  /**
   * Atualizar papel de colaborador da biblioteca
   * @param permissionId - ID da permissão
   * @param role - 'editor' | 'viewer'
   */
  async updateBibliotecaCollaboratorRole(
    permissionId: string,
    role: 'editor' | 'viewer'
  ): Promise<boolean> {
    const bibliotecaId = await this.getBibliotecaFolderId();
    if (!bibliotecaId) return false;

    const driveRole = role === 'editor' ? 'writer' : 'reader';
    return await this.updatePermissionRole(bibliotecaId, permissionId, driveRole);
  }

  /**
   * Tornar biblioteca privada (remover todos os colaboradores)
   */
  async makeBibliotecaPrivate(): Promise<boolean> {
    const collaborators = await this.getBibliotecaCollaborators();

    for (const collab of collaborators) {
      await this.removeBibliotecaCollaborator(collab.id);
    }

    return true;
  }

  /**
   * Compartilhar um PDF específico com alguém
   * @param driveFileId - ID do arquivo no Drive
   * @param email - Email do destinatário (opcional - se não passar, retorna só o link)
   */
  async sharePdf(
    driveFileId: string,
    email?: string
  ): Promise<{ link: string; permissionId?: string }> {
    // Primeiro, tornar o arquivo acessível via link
    await this.makeFileShareable(driveFileId);

    // Obter link compartilhável
    const link = await this.getFileShareableLink(driveFileId);

    // Se passou email, compartilhar diretamente
    let permissionId: string | undefined;
    if (email) {
      const result = await this.shareWithEmail(driveFileId, email, 'reader');
      permissionId = result || undefined;
    }

    return { link, permissionId };
  }

  /**
   * Listar bibliotecas Bigwa compartilhadas comigo
   * Procura por pastas "Biblioteca" dentro de pastas "Bigwa_App" de outros usuários
   */
  async getSharedBibliotecas(): Promise<Array<{
    id: string;
    name: string;
    ownerEmail: string;
    ownerName?: string;
  }>> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Buscar pastas chamadas "Biblioteca" compartilhadas comigo
    const query = "name='Biblioteca' and mimeType='application/vnd.google-apps.folder' and sharedWithMe=true and trashed=false";
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,name,owners)`;

    try {
      const response = await driveFetch(searchUrl, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to search shared bibliotecas: ${response.statusText}`);
      }

      const data = await response.json();

      return (data.files || []).map((f: { id: string; name: string; owners?: Array<{ emailAddress?: string; displayName?: string }> }) => ({
        id: f.id,
        name: f.name,
        ownerEmail: f.owners?.[0]?.emailAddress || '',
        ownerName: f.owners?.[0]?.displayName
      }));
    } catch (error) {
      logger.error('GoogleDriveSharing', 'getSharedBibliotecas error:', error);
      return [];
    }
  }

  /**
   * Carregar works de uma biblioteca compartilhada
   * Busca o arquivo library.json na pasta Biblioteca compartilhada
   */
  async loadSharedBibliotecaWorks(bibliotecaId: string): Promise<Record<string, unknown>[]> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    try {
      // Buscar library.json dentro da pasta Biblioteca
      const query = `name='library.json' and '${bibliotecaId}' in parents and trashed=false`;
      const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

      const searchResponse = await driveFetch(searchUrl, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!searchResponse.ok) {
        throw new Error(`Failed to search library.json: ${searchResponse.statusText}`);
      }

      const searchData = await searchResponse.json();

      if (!searchData.files || searchData.files.length === 0) {
        return [];
      }

      // Baixar conteúdo do library.json
      const fileId = searchData.files[0].id;
      const downloadUrl = `${DRIVE_API_BASE}/files/${fileId}?alt=media`;

      const downloadResponse = await driveFetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!downloadResponse.ok) {
        throw new Error(`Failed to download library.json: ${downloadResponse.statusText}`);
      }

      const libraryData = await downloadResponse.json();

      // O library.json pode ter estrutura { works: [...] } ou ser array direto
      const works = libraryData.works || libraryData || [];

      return works;
    } catch (error) {
      logger.error('GoogleDriveSharing', 'loadSharedBibliotecaWorks error:', error);
      return [];
    }
  }
}
